// src/lib/useAnimationGate.js
// Phase 47 — extracted from src/year-hero/BrandHero.jsx:16-78.
//
// Scope: composes IntersectionObserver + visibilitychange +
// matchMedia('(prefers-reduced-motion: reduce)') + localStorage
// 'tend:animationsEnabled' into a single data-anim-paused attribute value.
// Priority: user-disabled > reduced-motion > hidden > offscreen > running.
// Public exports: useAnimationGate (named export — returns [ref, reason]).
// Module state: none.
import { useEffect, useRef, useState } from 'react';

export function useAnimationGate() {
  const ref = useRef(null);
  const [reason, setReason] = useState(undefined);

  useEffect(() => {
    if (!ref.current) return;
    let offscreen = false;
    let hidden = typeof document !== 'undefined' && document.hidden === true;
    let reduced = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let userDisabled = (typeof localStorage !== 'undefined')
      && localStorage.getItem('tend:animationsEnabled') === 'false';

    const recompute = () => {
      if (userDisabled)   setReason('user-disabled');
      else if (reduced)   setReason('reduced-motion');
      else if (hidden)    setReason('hidden');
      else if (offscreen) setReason('offscreen');
      else                setReason(undefined);
    };

    // 1. IntersectionObserver — guard for jsdom (no IO in jsdom 29).
    let observer;
    if (typeof IntersectionObserver === 'function') {
      observer = new IntersectionObserver(([entry]) => {
        offscreen = !entry.isIntersecting;
        recompute();
      }, { threshold: 0 });
      observer.observe(ref.current);
    }

    // 2. visibilitychange.
    const onVis = () => {
      hidden = typeof document !== 'undefined' && document.hidden === true;
      recompute();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }

    // 3. prefers-reduced-motion live changes.
    let mq, mqHandler;
    if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
      mq = window.matchMedia('(prefers-reduced-motion: reduce)');
      mqHandler = (e) => { reduced = e.matches; recompute(); };
      // Older Safari uses addListener / removeListener (legacy); feature-detect.
      if (mq.addEventListener) mq.addEventListener('change', mqHandler);
      else if (mq.addListener) mq.addListener(mqHandler);
    }

    // 4. NEW (Phase 47): tend:animationsEnabled storage event.
    const onStorage = (e) => {
      if (e.key === 'tend:animationsEnabled') {
        userDisabled = e.newValue === 'false';
        recompute();
      }
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('storage', onStorage);
    }

    recompute();

    return () => {
      if (observer) observer.disconnect();
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
      if (mq) {
        if (mq.removeEventListener) mq.removeEventListener('change', mqHandler);
        else if (mq.removeListener) mq.removeListener(mqHandler);
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('storage', onStorage);
      }
    };
  }, []);

  return [ref, reason];
}
